import React from "react";

import "chart.js/auto";

import {
  Chart as ChartJS,
  /*  CategoryScale,
  LinearScale,
  PointElement,
  Bar, */
} from "chart.js";

import { Chart } from "react-chartjs-2";

// TODO: NEED TO IMPLEMENT TREESHAKING ABOVE. But currently causing a bug, probably related to: https://github.com/chartjs/Chart.js/issues/10163

import PropTypes from "prop-types";

import annotationPlugin from "chartjs-plugin-annotation";
import { Paper } from "@mui/material";

import { useScreenSizes } from "../Providers/ScreenSizesProvider";
import {
  secondaryColor,
  primaryColor,
} from "../../assets/jss/nextjs-material-kit-pro";

import {
  getPaceFromSpeed,
  getPaceUnit,
  getConvertedDistance,
  getConvertedDistanceAlertThreshold,
  useRenders,
} from "../../modules/utils";

ChartJS.register(
  /*   CategoryScale,
  LinearScale,
  PointElement,
  Bar, */
  /*  
  //  LineElement,
  // Title,
  Tooltip,
  Legend, */
  annotationPlugin
);

export const isActiveItem = (item) =>
  item?.itemDetails?.integrationId && !item.itemDetails.retired;
const getItemsToUse = (athleteProducts, limit) => {
  const itemsToUse = athleteProducts?.edges
    .map((a) =>
      a.items ? a.items.map((i) => ({ ...i, linkedProduct: a.product })) : []
    )
    .flat()
    .filter(isActiveItem)
    .sort((a, b) => {
      // Strava integrationIds are incremental, so we can sort by that
      // Make the most recent (highest number) first
      // the string always starts with a g followed by an integer eg g12735456

      // Extract the numeric part of the strings
      const numA = parseInt(a.itemDetails.integrationId.substring(1));
      const numB = parseInt(b.itemDetails.integrationId.substring(1));

      // Compare the numbers in descending order
      return numB - numA;
    });

  if (limit && itemsToUse?.length > limit) {
    itemsToUse.length = limit;
  }

  return itemsToUse;
};

const GearOverview = (props) => {
  const { user, noBorder, limit, limitAspectRatio } = props;
  useRenders("gearOverview");
  const {
    communicationPreferences: { measurementPreference, distanceAlertThreshold },
    athleteProducts,
  } = user;
  const distanceColor = primaryColor;
  const paceColor = secondaryColor;
  const { isXsScreen, isSmScreen, isLgScreen, isXlScreen } = useScreenSizes();

  const convertedDistanceAlertThreshold = getConvertedDistanceAlertThreshold(
    distanceAlertThreshold,
    measurementPreference
  );

  const renderPaceValue = (value, showUnit = false) =>
    getPaceFromSpeed(value, measurementPreference, showUnit);
  /**
   * Takes a string phrase and breaks it into separate phrases
   * no bigger than 'maxwidth', breaks are made at complete words.
   */
  function formatLabel(str, maxwidth) {
    var sections = [];
    var words = str.split(" ");
    var temp = "";

    words.forEach(function (item, index) {
      if (temp.length > 0) {
        var concat = temp + " " + item;

        if (concat.length > maxwidth) {
          sections.push(temp);
          temp = "";
        } else {
          if (index == words.length - 1) {
            sections.push(concat);
            return;
          } else {
            temp = concat;
            return;
          }
        }
      }

      if (index == words.length - 1) {
        sections.push(item);
        return;
      }

      if (item.length < maxwidth) {
        temp = item;
      } else {
        sections.push(item);
      }
    });

    return sections;
  }
  const id = "gear-overview";

  const itemsToUse = getItemsToUse(athleteProducts, limit);

  if (!itemsToUse) {
    return <div id={id} />;
  }

  const hasPace = itemsToUse.some((i) => i.itemStats?.averages.speed);

  const labels = itemsToUse.map((item) =>
    formatLabel(item?.linkedProduct?.title || item.itemDetails?.modelName, 20)
  );

  const distanceData = itemsToUse.map((item) =>
    getConvertedDistance(
      item.itemStats?.distance || 0,
      measurementPreference,
      false
    )
  );
  const paceData = hasPace
    ? itemsToUse.map((item) => item.itemStats?.averages?.speed)
    : null;

  const datasets = [
    {
      type: "bar",
      label: "Miles",
      backgroundColor: distanceColor + 75,
      data: distanceData,
      xAxisID: "x",
      order: 1,
      borderRadius: 6,
      borderSkipped: "left",
    },
  ];

  if (hasPace) {
    datasets.unshift({
      type: "scatter",
      label: "Pace",
      data: paceData,
      backgroundColor: paceColor,
      borderColor: "white",
      borderWidth: 3,
      xAxisID: "x1",
      order: 0,
    });
  }

  const data = {
    labels: labels,
    datasets,
  };

  const getGaugeBoxes = () => {
    const boxes = {};
    for (const [index, distance] of distanceData.entries()) {
      const isOver = distance > convertedDistanceAlertThreshold;
      const obj = {
        type: "box",
        backgroundColor: isOver ? secondaryColor + 80 : primaryColor + 20,
        borderWidth: 0,
        borderRadius: isOver ? { topRight: 6, bottomRight: 6 } : null,
        xMin: !isOver ? 0 : convertedDistanceAlertThreshold,
        xMax: !isOver ? convertedDistanceAlertThreshold : distance,
        yMin: index - 0.36,
        yMax: index + 0.36,
      };

      boxes[`line${index}`] = obj;
    }
    return boxes;
  };

  const gaugeBoxes = getGaugeBoxes();

  const config = {
    type: "bar",
    data: data,
    options: {
      responsive: true,
      aspectRatio: !limitAspectRatio
        ? isSmScreen
          ? 1
          : 2
        : isXsScreen
        ? 1
        : 2,
      indexAxis: "y",
      plugins: {
        title: {
          display: !isLgScreen || isSmScreen || (!isLgScreen && isXlScreen),
          text: "Your gear",
          font: { size: 20 },
        },
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (context) =>
              itemsToUse[context[0]?.dataIndex]?.itemDetails.modelName ||
              context[0].label,
            label: (context) =>
              context.dataset.label === "Pace"
                ? `Av. pace: ${renderPaceValue(context.raw, true)}`
                : `Distance run: ${
                    context.formattedValue
                  } ${measurementPreference} (${Math.round(
                    (context.raw / convertedDistanceAlertThreshold) * 100
                  )}%)`,
            labelColor: (context) => ({
              backgroundColor:
                context.dataset.label === "Pace"
                  ? "lightblue"
                  : context.raw > convertedDistanceAlertThreshold
                  ? secondaryColor
                  : distanceColor,
            }),
          },
        },
        annotation: {
          annotations: {
            ...gaugeBoxes,
            distanceAlertThreshold: {
              type: "line",
              borderColor: "black",
              borderWidth: 3,
              scaleID: "x",
              value: convertedDistanceAlertThreshold,
              label: {
                display: true,
                backgroundColor: "black",
                borderColor: "black",
                borderRadius: 6,
                borderWidth: 2,
                content: () => "Replace?",
                rotation: "auto",
              },
            },
          },
        },
      },
      elements: { point: { radius: 6 } },
      scales: {
        y: {
          type: "category",
          position: "left",
          ticks: { mirror: isXsScreen },
          grid: { display: false },
        },
        x: {
          title: {
            display: true,
            text: `Distance run (${measurementPreference})`,
          },

          type: "linear",
          display: true,
          position: "top",
        },
      },
    },
  };

  if (hasPace) {
    const minSpeed = Math.min(...paceData.filter(Boolean));

    config.options.scales.x1 = {
      title: {
        display: "true",
        text: "Average pace" + " (" + getPaceUnit(measurementPreference) + ")",
      },
      type: "linear",
      display: true,
      position: "bottom",
      beginAtZero: false,
      min: minSpeed * 0.9,
      ticks: {
        callback: (val) => renderPaceValue(val),
      },
      grid: {
        drawOnChartArea: false, // only want the grid lines for one axis to show up
      },
    };
  }
  return noBorder ? (
    <Chart {...config} id={id} />
  ) : (
    <Paper
      id={id}
      variant="outlined"
      sx={{
        p: isXsScreen ? 0.5 : isSmScreen ? 1 : 2,
        mb: 2,
      }}
    >
      <Chart {...config} />
    </Paper>
  );
};

GearOverview.defaultProps = { limit: 15 };
GearOverview.propTypes = {
  measurementPreference: PropTypes.oneOf(["km", "mi"]),
  limit: PropTypes.number,
};

export default GearOverview;
